import React, { Suspense, lazy, useEffect, useState } from 'react';
import { initGA, trackPageView } from './lib/analytics';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import MaintenancePage from './features/Maintenance/MaintenancePage';
import { maintenanceService, MaintenanceStatus } from './services/maintenance';
import { GoogleOAuthProvider } from '@react-oauth/google';
import { ENV_CONFIG } from './config/env';
import Header from './components/layout/Header';
import Footer from './components/layout/Footer';
import BottomNav from './components/layout/BottomNav';
import ProtectedRoute from './components/auth/ProtectedRoute';
import AuthenticatedRedirect from './components/auth/AuthenticatedRedirect';
import { AuthProvider } from './contexts/AuthContext';
import { NotificationPrompt } from './components/ui/NotificationPrompt';
import { ToastContainer } from 'react-toastify';
import { registerSW } from 'virtual:pwa-register';
import 'react-toastify/dist/ReactToastify.css';
import './styles/index.scss';

const HomePage = lazy(() => import('./features/Home/HomePage'));
const ProgramsPage = lazy(() => import('./features/Programs/ProgramsPage'));
const ProgramDetailPage = lazy(() => import('./features/ProgramDetail/ProgramDetailPage'));
const DonatePage = lazy(() => import('./features/Donate/DonatePage'));
const PaymentPage = lazy(() => import('./features/Payment/PaymentPage'));
const AboutPage = lazy(() => import('./features/About/AboutPage'));
const ContactPage = lazy(() => import('./features/Contact/ContactPage'));
const HowToDonatePage = lazy(() => import('./features/HowToDonate/HowToDonatePage'));
const FAQPage = lazy(() => import('./features/FAQ/FAQPage'));
const PrivacyPage = lazy(() => import('./features/Privacy/PrivacyPage'));
const TermsPage = lazy(() => import('./features/Terms/TermsPage'));
const OrganizationPage = lazy(() => import('./features/Organization/OrganizationPage'));
const VisionMissionPage = lazy(() => import('./features/VisionMission/VisionMissionPage'));
const NewsPage = lazy(() => import('./features/News/NewsPage'));
const NewsDetailPage = lazy(() => import('./features/News/NewsDetailPage'));
const FinancialReportPage = lazy(() => import('./features/FinancialReport/FinancialReportPage'));
const AnnualReportPage = lazy(() => import('./features/AnnualReport/AnnualReportPage'));
const ReportDetailPage = lazy(() => import('./features/Report/ReportDetailPage'));
const LoginPage = lazy(() => import('./features/Auth/LoginPage'));
const RegisterPage = lazy(() => import('./features/Auth/RegisterPage'));
const ForgotPasswordPage = lazy(() => import('./features/Auth/ForgotPasswordPage'));
const ResetPasswordPage = lazy(() => import('./features/ResetPassword/ResetPasswordPage'));
const ProfilePage = lazy(() => import('./features/Profile/ProfilePage'));
const SettingsPage = lazy(() => import('./features/Settings/SettingsPage'));
const MyDonationHistoryPage = lazy(() => import('./features/MyDonationHistory/MyDonationHistoryPage'));
const DonationSuccessPage = lazy(() => import('./features/DonationSuccess/DonationSuccessPage'));
const NotFoundPage = lazy(() => import('./features/NotFound/NotFoundPage'));
const ZakatCalculatorPage = lazy(() => import('./features/ZakatCalculator/ZakatCalculatorPage'));
const PublicDashboardPage = lazy(() => import('./features/PublicDashboard/PublicDashboardPage'));
const DistributionDetailPage = lazy(() => import('./features/Distribution/DistributionDetailPage'));

// Skip link component for accessibility
const SkipLink = () => (
  <a
    href="#main-content"
    className="skip-link"
  >
    Lewati ke konten utama
  </a>
);

// Layout component with semantic HTML and ARIA attributes
const Layout = ({ children }: { children: React.ReactNode }) => {
  const location = useLocation();
  const isPaymentPage = location.pathname.includes('/invoice/');
  const isDonatePage = location.pathname.includes('/donate/');

  // Announce page changes to screen readers
  useEffect(() => {
    const announcement = document.createElement('div');
    announcement.setAttribute('aria-live', 'polite');
    announcement.setAttribute('aria-atomic', 'true');
    announcement.setAttribute('class', 'sr-only');
    announcement.style.cssText =
      'position: absolute; width: 1px; height: 1px; padding: 0; margin: -1px; overflow: hidden; clip: rect(0,0,0,0); white-space: nowrap; border: 0;';
    document.body.appendChild(announcement);

    const pageName = location.pathname.split('/').pop() || 'beranda';
    announcement.textContent = `Halaman ${pageName} dimuat`;

    return () => {
      document.body.removeChild(announcement);
    };
  }, [location]);

  return (
    <>
      <SkipLink />
      {!isPaymentPage && !isDonatePage && <Header />}
      <main
        id="main-content"
        className="main-content"
        role="main"
        aria-label="Konten utama"
        tabIndex={-1}
      >
        {children}
      </main>
      {!isPaymentPage && !isDonatePage && !location.pathname.startsWith('/login') && !location.pathname.startsWith('/register') && !location.pathname.startsWith('/forgot-password') && !location.pathname.startsWith('/reset-password') && <Footer />}
      {!isPaymentPage && !isDonatePage && <BottomNav />}
    </>
  );
};

// Page title component for accessibility
const PageTitle = ({ title }: { title: string }) => {
  useEffect(() => {
    document.title = `${title} - LAZISMU UMMI`;
  }, [title]);

  return null;
};

const RouteFallback = () => (
  <div className="min-h-[40vh] flex items-center justify-center text-sm text-slate-500">
    Memuat halaman...
  </div>
);

// Focus management and scroll to top for route changes
const FocusManager = () => {
  const location = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
    const mainContent = document.getElementById('main-content');
    if (mainContent) {
      mainContent.focus({ preventScroll: true });
    }
    trackPageView(location.pathname + location.search);
  }, [location.pathname, location.search]);

  return null;
};

// Checks maintenance status on mount, renders maintenance page when active
function AppContent() {
  const [maintenance, setMaintenance] = useState<MaintenanceStatus | null>(null);
  const [checked, setChecked] = useState(false);

  useEffect(() => {
    maintenanceService.getStatus()
      .then(status => setMaintenance(status))
      .catch(() => {})
      .finally(() => setChecked(true));
  }, []);

  if (!checked) return null;

  if (maintenance?.isActive) {
    return <MaintenancePage maintenance={maintenance} />;
  }

  return (
    <>
      <FocusManager />
      <NotificationPrompt />
      <ToastContainer
        position="bottom-right"
        autoClose={4000}
        hideProgressBar
        newestOnTop
        closeOnClick
        pauseOnFocusLoss
        pauseOnHover
        draggable={false}
        className="app-toast-container"
        toastClassName="app-toast"
        bodyClassName="app-toast-body"
      />
      <Suspense fallback={<RouteFallback />}>
      <Routes>
        <Route path="/" element={
          <Layout>
            <PageTitle title="Beranda" />
            <HomePage />
          </Layout>
        } />
        <Route path="/programs" element={
          <Layout>
            <PageTitle title="Program Donasi" />
            <ProgramsPage />
          </Layout>
        } />
        <Route path="/dashboard" element={
          <Layout>
            <PageTitle title="Dashboard Publik" />
            <PublicDashboardPage />
          </Layout>
        } />
        <Route path="/distributions/:id" element={
          <Layout>
            <PageTitle title="Detail Penyaluran" />
            <DistributionDetailPage />
          </Layout>
        } />
        <Route path="/program/:slug" element={
          <Layout>
            <PageTitle title="Detail Program" />
            <ProgramDetailPage />
          </Layout>
        } />
        <Route path="/donate/:slug" element={
          <Layout>
            <PageTitle title="Donasi" />
            <DonatePage />
          </Layout>
        } />
        <Route path="/donate/:slug/invoice/:orderId" element={
          <>
            <PageTitle title="Pembayaran" />
            <PaymentPage />
          </>
        } />
        <Route path="/about" element={
          <Layout>
            <PageTitle title="Tentang Kami" />
            <AboutPage />
          </Layout>
        } />
        <Route path="/contact" element={
          <Layout>
            <PageTitle title="Kontak" />
            <ContactPage />
          </Layout>
        } />
        <Route path="/how-to-donate" element={
          <Layout>
            <PageTitle title="Cara Donasi" />
            <HowToDonatePage />
          </Layout>
        } />
        <Route path="/faq" element={
          <Layout>
            <PageTitle title="FAQ" />
            <FAQPage />
          </Layout>
        } />
        <Route path="/privacy-policy" element={
          <Layout>
            <PageTitle title="Kebijakan Privasi" />
            <PrivacyPage />
          </Layout>
        } />
        <Route path="/terms" element={
          <Layout>
            <PageTitle title="Syarat dan Ketentuan" />
            <TermsPage />
          </Layout>
        } />
        <Route path="/organization" element={
          <Layout>
            <PageTitle title="Struktur Organisasi" />
            <OrganizationPage />
          </Layout>
        } />
        <Route path="/vision-mission" element={
          <Layout>
            <PageTitle title="Visi, Misi & Prinsip" />
            <VisionMissionPage />
          </Layout>
        } />
        <Route path="/news" element={
          <Layout>
            <PageTitle title="Berita" />
            <NewsPage />
          </Layout>
        } />
        <Route path="/news/:slug" element={
          <Layout>
            <PageTitle title="Detail Berita" />
            <NewsDetailPage />
          </Layout>
        } />
        <Route path="/financial-report" element={
          <Layout>
            <PageTitle title="Laporan Keuangan" />
            <FinancialReportPage />
          </Layout>
        } />
        <Route path="/financial-report/:slug" element={
          <Layout>
            <PageTitle title="Detail Laporan Keuangan" />
            <ReportDetailPage type="financial-report" />
          </Layout>
        } />
        <Route path="/annual-report" element={
          <Layout>
            <PageTitle title="Laporan Tahunan" />
            <AnnualReportPage />
          </Layout>
        } />
        <Route path="/annual-report/:slug" element={
          <Layout>
            <PageTitle title="Detail Laporan Tahunan" />
            <ReportDetailPage type="annual-report" />
          </Layout>
        } />
        <Route path="/zakat-calculator" element={
          <Layout>
            <PageTitle title="Kalkulator Zakat" />
            <ZakatCalculatorPage />
          </Layout>
        } />
        <Route path="/login" element={
          <Layout>
            <PageTitle title="Masuk" />
            <AuthenticatedRedirect>
              <LoginPage />
            </AuthenticatedRedirect>
          </Layout>
        } />
        <Route path="/register" element={
          <Layout>
            <PageTitle title="Daftar" />
            <AuthenticatedRedirect>
              <RegisterPage />
            </AuthenticatedRedirect>
          </Layout>
        } />
        <Route path="/forgot-password" element={
          <Layout>
            <PageTitle title="Lupa Password" />
            <ForgotPasswordPage />
          </Layout>
        } />
        <Route path="/reset-password" element={
          <Layout>
            <PageTitle title="Atur Ulang Password" />
            <ResetPasswordPage />
          </Layout>
        } />
        <Route path="/profile" element={
          <Layout>
            <PageTitle title="Profil Saya" />
            <ProtectedRoute>
              <ProfilePage />
            </ProtectedRoute>
          </Layout>
        } />
        <Route path="/settings" element={
          <Layout>
            <PageTitle title="Pengaturan" />
            <ProtectedRoute>
              <SettingsPage />
            </ProtectedRoute>
          </Layout>
        } />
        <Route path="/my-donation-history" element={
          <Layout>
            <PageTitle title="Riwayat Donasi Saya" />
            <ProtectedRoute>
              <MyDonationHistoryPage />
            </ProtectedRoute>
          </Layout>
        } />
        <Route path="/donation/success/:orderId" element={
          <Layout>
            <PageTitle title="Donasi Berhasil" />
            <DonationSuccessPage />
          </Layout>
        } />
        <Route path="*" element={
          <Layout>
            <PageTitle title="Halaman Tidak Ditemukan" />
            <NotFoundPage />
          </Layout>
        } />
      </Routes>
      </Suspense>
    </>
  );
}

function App() {
  useEffect(() => {
    initGA();
  }, []);

  // Register PWA service worker
  useEffect(() => {
    const updateSW = registerSW({
      onNeedRefresh() {
        // Only show update prompt in production
        if (import.meta.env.PROD) {
          if (confirm('Ada update baru tersedia. Muat ulang sekarang?')) {
            updateSW(true);
          }
        } else {
          // Auto-update in development without prompting
          console.log('Development: Auto-updating service worker');
          updateSW(true);
        }
      },
      onOfflineReady() {
        console.log('App ready to work offline');
      },
    });
  }, []);

  return (
    <GoogleOAuthProvider clientId={ENV_CONFIG.GOOGLE_CLIENT_ID}>
      <AuthProvider>
        <Router>
          <AppContent />
        </Router>
      </AuthProvider>
    </GoogleOAuthProvider>
  );
}

export default App;
